import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import isEqual from 'react-fast-compare'
import type { DiscountCoupon } from '~/lib/graphql/graphql'
import type { DateText, HourMinute, PlanId } from '~/lib/graphql/scalars'
import { scope } from '../scope'
import type { SelectedDiscountCouponType } from './statemachine'
import { reservationInputMachine } from './statemachine'

export const reservationInputState$ = scope.atom(
  reservationInputMachine.initialState,
  'reservationInputState$'
)

export const reservationInputStateValue$ = selectAtom(
  reservationInputState$,
  (state) => state.value
)
reservationInputStateValue$.debugLabel = 'reservationInputStateValue$'

// RESERVE_SUCCESS も isReserving に含めることで予約完了後画面遷移の際の違和感をなくす
// https://github.com/ikyucom/macaron-frontend/pull/5193#discussion_r1893380984
const isReserving$ = atom((get) =>
  ['RESERVING', 'RESERVE_SUCCESS'].includes(get(reservationInputState$).value)
)

export function useIsReserving() {
  return useAtomValue(isReserving$)
}

export const restaurantId$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.restaurantId
)
restaurantId$.debugLabel = 'restaurantId$'

export function useRestaurantId() {
  return useAtomValue(restaurantId$)
}

export const visitorsCount$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.visitorsCount ?? 0
)
visitorsCount$.debugLabel = 'visitorsCount$'

export function useVisitorsCount() {
  return useAtomValue(visitorsCount$)
}

export const visitDate$: Atom<DateText | undefined> = selectAtom(
  reservationInputState$,
  (state) => state.context?.visitDate
)
visitDate$.debugLabel = 'visitDate$'

export function useVisitDate() {
  return useAtomValue(visitDate$)
}

export const visitTime$: Atom<HourMinute | undefined> = selectAtom(
  reservationInputState$,
  (state) => state.context?.visitTime
)
visitTime$.debugLabel = 'visitTime$'

export function useVisitTime() {
  return useAtomValue(visitTime$)
}

export const planId$: Atom<PlanId | undefined> = selectAtom(
  reservationInputState$,
  (state) => state.context?.planId
)
planId$.debugLabel = 'planId$'

export function usePlanId() {
  return useAtomValue(planId$)
}

export const userType$ = selectAtom(reservationInputState$, (state) => state.context?.userType)
userType$.debugLabel = 'userType$'

export const isTrusted$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.isTrusted ?? false
)
isTrusted$.debugLabel = 'isTrusted$'

export const childCount$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.childCount ?? 0
)
childCount$.debugLabel = 'childCount$'

export function useChildCount() {
  return useAtomValue(childCount$)
}

export const selectedChildMenus$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.childMenus ?? [],
  (prev, next) => isEqual(prev, next)
)
selectedChildMenus$.debugLabel = 'selectedChildMenus$'

export function useSelectedChildMenus() {
  return useAtomValue(selectedChildMenus$)
}

export const discountCouponType$: Atom<SelectedDiscountCouponType | undefined> = selectAtom(
  reservationInputState$,
  // 他の context では jotai に持ってくる際は null を undefined にするように変換しているので
  // discountType$ も同様に undefined に変換
  (state) => state.context?.discountCouponType ?? undefined
)
discountCouponType$.debugLabel = 'discountCouponType$'

export function useDiscountCouponType() {
  return useAtomValue(discountCouponType$)
}

export const appliedDiscountCoupon$: Atom<DiscountCoupon | undefined> = selectAtom(
  reservationInputState$,
  (state) => state.context?.appliedDiscountCoupon ?? undefined
)
appliedDiscountCoupon$.debugLabel = 'AppliedDiscountCoupon$'

export function useAppliedDiscountCoupon() {
  return useAtomValue(appliedDiscountCoupon$)
}

export const selectedOptionMenus$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.optionMenus ?? [],
  (prev, next) => isEqual(prev, next)
)
selectedOptionMenus$.debugLabel = 'selectedOptionMenus$'

export function useSelectedOptionMenus() {
  return useAtomValue(selectedOptionMenus$)
}

/** ユーザーが能動的に選択した獲得ポイント利用方法 */
export const userSelectedGivenPointsUsage$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.givenPointsUsage
)
userSelectedGivenPointsUsage$.debugLabel = 'userSelectedGivenPointsUsage$'

export const selectedGivenPointsType$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.givenPointsType ?? 'IKYU'
)
selectedGivenPointsType$.debugLabel = 'selectedGivenPointsType$'

export function useSelectedGivenPointsType() {
  return useAtomValue(selectedGivenPointsType$)
}

export const paypayPointUsage$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.paypayPointUsage ?? 0
)
paypayPointUsage$.debugLabel = 'paypayPointUsage$'

export const authResult$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.authResult ?? undefined
)
authResult$.debugLabel = 'authResult$'

export const reservationResult$ = selectAtom(
  reservationInputState$,
  (state) => state.context?.reservationResult ?? undefined
)
reservationResult$.debugLabel = 'reservationResult$'

export function useReservationResult() {
  return useAtomValue(reservationResult$)
}

export const seatId$ = selectAtom(reservationInputState$, (s) => s.context?.seatId ?? undefined)
seatId$.debugLabel = 'seatId$'

export function useSelectedSeatId() {
  return useAtomValue(seatId$)
}

export const reserverNameKana$: Atom<string> = selectAtom(
  reservationInputState$,
  (state) => state.context?.reserverNameKana ?? ''
)
reserverNameKana$.debugLabel = 'reserverNameKana$'

export function useReserverNameKana() {
  return useAtomValue(reserverNameKana$)
}

export const reserverPhoneNumber$: Atom<string> = selectAtom(
  reservationInputState$,
  (state) => state.context?.reserverPhoneNumber ?? ''
)
reserverPhoneNumber$.debugLabel = 'reserverPhoneNumber$'

export function useReserverPhoneNumber() {
  return useAtomValue(reserverPhoneNumber$)
}

export const email$: Atom<string> = selectAtom(
  reservationInputState$,
  (state) => state.context?.email ?? ''
)
email$.debugLabel = 'email$'

export function useEmail() {
  return useAtomValue(email$)
}

export const password$: Atom<string> = selectAtom(
  reservationInputState$,
  (state) => state.context?.password ?? ''
)
password$.debugLabel = 'password$'

export const ikyuPointUsage$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.ikyuPointUsage ?? 0
)
ikyuPointUsage$.debugLabel = 'ikyuPointUsage$'

export const visitorNameKana$: Atom<string> = selectAtom(
  reservationInputState$,
  (s) => s.context?.visitorNameKana ?? ''
)
visitorNameKana$.debugLabel = 'visitorNameKana$'

export const visitorPhoneNumber$: Atom<string> = selectAtom(
  reservationInputState$,
  (s) => s.context?.visitorPhoneNumber ?? ''
)
visitorPhoneNumber$.debugLabel = 'visitorPhoneNumber$'

export const isReserverDifferentFromVisitor$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.isReserverDifferentFromVisitor ?? false
)
isReserverDifferentFromVisitor$.debugLabel = 'isReserverDifferentFromVisitor$'

export function useIsReserverDifferentFromVisitor() {
  return useAtomValue(isReserverDifferentFromVisitor$)
}

export const card$ = selectAtom(reservationInputState$, (s) => s.context?.card ?? undefined)
card$.debugLabel = 'card$'

export function useCard() {
  return useAtomValue(card$)
}

export const signupOnly$ = selectAtom(reservationInputState$, (s) => s.context?.signupOnly ?? false)
signupOnly$.debugLabel = 'signupOnly$'

export function useSignupOnly() {
  return useAtomValue(signupOnly$)
}

export const affiliateSiteFormAnswer$: Atom<string> = selectAtom(
  reservationInputState$,
  (s) => s.context?.affiliateSiteFormAnswer ?? ''
)
affiliateSiteFormAnswer$.debugLabel = 'affiliateSiteFormAnswer$'

export function useAffiliateSiteFormAnswer() {
  return useAtomValue(affiliateSiteFormAnswer$)
}

export const requestsAndComments$: Atom<string> = selectAtom(
  reservationInputState$,
  (s) => s.context?.requestsAndComments ?? ''
)
requestsAndComments$.debugLabel = 'requestsAndComments$'

export const agreedToReserveRequest$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.agreedToReserveRequest ?? false
)
agreedToReserveRequest$.debugLabel = 'agreedToReserveRequest$'

export function useAgreedToReserveRequest() {
  return useAtomValue(agreedToReserveRequest$)
}

export const subscribeMailMagazineIkyuNews$: Atom<boolean | undefined> = selectAtom(
  reservationInputState$,
  (s) => s.context?.subscribeMailMagazineIkyuNews ?? undefined
)
subscribeMailMagazineIkyuNews$.debugLabel = 'subscribeMailMagazineIkyuNews$'

export function useSubscribeMailMagazineIkyuNews() {
  return useAtomValue(subscribeMailMagazineIkyuNews$)
}

export const subscribeMailMagazinePointCoupon$: Atom<boolean | undefined> = selectAtom(
  reservationInputState$,
  (s) => s.context?.subscribeMailMagazinePointCoupon ?? undefined
)
subscribeMailMagazinePointCoupon$.debugLabel = 'subscribeMailMagazinePointCoupon$'

export function useSubscribeMailMagazinePointCoupon() {
  return useAtomValue(subscribeMailMagazinePointCoupon$)
}

export const isIkyuPointEnabled$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.isIkyuPointEnabled ?? false
)
isIkyuPointEnabled$.debugLabel = 'isIkyuPointEnabled$'

export function useIsIkyuPointEnabled() {
  return useAtomValue(isIkyuPointEnabled$)
}

export const isDiscountCouponEnabled$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.isDiscountCouponEnabled ?? false
)
isDiscountCouponEnabled$.debugLabel = 'isDiscountCouponEnabled$'

export function useIsDiscountCouponEnabled() {
  return useAtomValue(isDiscountCouponEnabled$)
}

export const isPayPayPointEnabled$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.isPayPayPointEnabled ?? false
)
isPayPayPointEnabled$.debugLabel = 'isPayPayPointEnabled$'

export function useIsPayPayPointEnabled() {
  return useAtomValue(isPayPayPointEnabled$)
}

export const paymentMethod$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.paymentMethod ?? 'ONSITE'
)
paymentMethod$.debugLabel = 'paymentMethod$'

export function usePaymentMethod() {
  return useAtomValue(paymentMethod$)
}

export const isAllIkyuPointSelected$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.isAllIkyuPointSelected ?? false
)

export function useIsAllIkyuPointSelected() {
  return useAtomValue(isAllIkyuPointSelected$)
}

export const isAllPayPayPointSelected$ = selectAtom(
  reservationInputState$,
  (s) => s.context?.isAllPayPayPointSelected ?? false
)

export function useIsAllPayPayPointSelected() {
  return useAtomValue(isAllPayPayPointSelected$)
}
